import { c as _c } from "react/compiler-runtime";
import { Button, Divider, IconButton, Stack, Typography } from '@mui/material';
import { Provider, VaultStatus } from '@repo/api-gw-sdk';
import { useCallback, useState } from 'react';
import { AccountIndicator } from '@/components/accounts/accountIndicator';
import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { CopyToClipboardWrapper } from '@/components/buttons/copyToClipboardButton';
import { Icon } from '@/components/shared/icon';
import { TagsElasticContainer } from '@/components/tags/tagsElasticContainer';
import { TagsSection } from '@/components/tags/tagsSection';
import { OverflowTooltip } from '@/components/tooltips/overflowTooltip';
import { useDAL } from '@/contexts/useDAL';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { useRoles } from '@/contexts/useRoles';
import { useWorkspace } from '@/contexts/useWorkspace';
import { CloudProviders } from '@/data/inventory/data';
import type { BackupVault } from '@/data/vaults/backupVault';
import { getApiErrorMessage } from '@/utils/apiError';
import { VaultColorSelectorWithButtons } from './createBackupVaultPanel';
import { GcpKmsCopyablePolicy } from './gcpKmsCopyablePolicy';
import { KmsSuggestedPolicyContent } from './kmsSuggestedPolicy';
import { useBackupVaultColorUpdate } from './useBackupVaultColorUpdate';
import { VaultColoredDot } from './vaultColoredDot';
import { RenameEntityName } from '../wizard/renameEntityName';

// A vault carries a key only when the customer supplied one; without it the vault is encrypted with
// Eon's own key. Only the vault's own provider block can hold one, so any of them answers for it.
const hasCustomerEncryptionKey = (vault: BackupVault) => !!(vault.vaultAttributes.aws?.encryptionKey || vault.vaultAttributes.azure?.encryptionKey || vault.vaultAttributes.gcp?.encryptionKey);
interface UseVaultDetailsDialogOptions {
  editable: boolean;
  onSaveSuccess?: () => void;
}
export function useVaultDetailsDialog(t0) {
  const $ = _c(5);
  const isLoading = t0 === undefined ? false : t0;
  const {
    sharedDialog
  } = useWorkspace();
  let t1;
  if ($[0] !== isLoading || $[1] !== sharedDialog) {
    t1 = (vault, options) => {
      const openKmsSuggestedPolicy = () => {
        sharedDialog.open("vault-details-kms-policy", <KmsSuggestedPolicyContent kmsKey={vault.vaultAttributes.aws?.encryptionKey} cloudProvider={vault.vaultAttributes.cloudProvider} />, {
          isLoading,
          header: {
            title: "Get suggested policy",
            subtitle: "To restore from your self-managed vault, apply this policy in your AWS account. Without it, restoring can fail."
          }
        });
      };
      const openGcpKmsSuggestedPolicy = () => {
        sharedDialog.open("vault-details-gcp-kms-policy", <GcpKmsSuggestedPolicyContent backupVault={vault} />, {
          isLoading,
          header: {
            title: "Get suggested permissions",
            subtitle: "Run these commands to grant the required service accounts access to your Cloud KMS key. Without these permissions, vault operations may fail."
          }
        });
      };
      sharedDialog.open("vault-details", <VaultDetailsContent vault={vault} vaultAccountId={vault.vaultAccountId} editable={options.editable} onSaveSuccess={options.onSaveSuccess ?? _temp} onOpenKmsSuggestedPolicy={openKmsSuggestedPolicy} onOpenGcpKmsSuggestedPolicy={openGcpKmsSuggestedPolicy} />, {
        isLoading,
        header: {
          title: "Vault Details",
          subtitle: "View the vault's configuration. Only the name, color and tags can be edited."
        }
      });
    };
    $[0] = isLoading;
    $[1] = sharedDialog;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const open = t1;
  let t2;
  if ($[3] !== open) {
    t2 = {
      open
    };
    $[3] = open;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function _temp() {}
const VaultDetailsContent = ({
  vault: initialVault,
  vaultAccountId,
  editable,
  onSaveSuccess,
  onOpenKmsSuggestedPolicy,
  onOpenGcpKmsSuggestedPolicy
}: {
  vault: BackupVault;
  vaultAccountId?: string;
  editable: boolean;
  onSaveSuccess: () => void;
  onOpenKmsSuggestedPolicy: () => void;
  onOpenGcpKmsSuggestedPolicy: () => void;
}) => {
  const [vault, setVault] = useState(initialVault);
  const [isReapplyingPolicy, setIsReapplyingPolicy] = useState(false);
  const {
    snackbar
  } = useWorkspace();
  const [isEditingColor, setIsEditingColor] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedTags, setEditedTags] = useState<Record<string, string>>();
  const [areTagsValid, setAreTagsValid] = useState(true);
  const {
    isAuthorized
  } = useRoles();
  const isEditable = editable && isAuthorized('update:vaults');
  const dal = useDAL();
  const {
    updateVaultColor
  } = useBackupVaultColorUpdate();
  const {
    EnableMrapForByov: enableMrapForByov = false,
    EnableCspAssistedImmutability = false
  } = useFeatureFlags();
  const vaultAccounts = dal.cloudAccounts.vault.useList();
  const vaultAccount = vaultAccounts.data?.accounts.find(account => account.id === vaultAccountId);
  const canReapplyPolicy = vaultAccount?.isCustomerOwned && vault.vaultAttributes.cloudProvider === Provider.Aws && vault.status === VaultStatus.VAULT_STATUS_ACTIVE && !vault.vaultAttributes.aws?.bucketPrefix && vault.vaultAttributes.aws?.bucketName === `eon-vault-${vaultAccount.providerAccountId}-${vault.region}-${vault.id.slice(0, 5)}`;
  async function reapplyBucketPolicy() {
    setIsReapplyingPolicy(true);
    try {
      await dal.vaults.reconcileBucketPolicy(vault.id);
      snackbar.showMessage('Bucket policy reapplied successfully');
    } catch (error) {
      snackbar.showErrorMessage(getApiErrorMessage(error, 'Failed to reapply bucket policy. For help, contact support.'));
    } finally {
      setIsReapplyingPolicy(false);
    }
  }
  const accountLabel = vaultAccount?.cloudProvider === Provider.Azure ? 'Subscription' : vaultAccount?.cloudProvider === Provider.Gcp ? 'Project' : 'Account';
  return <Stack divider={<Divider sx={{
    borderStyle: 'dashed'
  }} />} sx={{
    marginBottom: '16px',
    gap: '16px'
  }}>
      <RowWrapper>
        <Typography sx={{
        width: '150px'
      }}>
          Name
        </Typography>
        {isEditable ? <RenameEntityName name={vault.name} onSubmit={name => {
        const updatedVault = {
          ...vault,
          name
        };
        void dal.vaults.update(updatedVault).then(() => {
          setVault(updatedVault);
          setIsEditingName(false);
          onSaveSuccess();
        });
      }} bold={false} editMode={isEditingName} setEditMode={setIsEditingName} /> : <Typography>{vault.name}</Typography>}
      </RowWrapper>
      <RowWrapper>
        <Typography sx={{
        width: '150px'
      }}>
          ID
        </Typography>
        <Stack direction='row' sx={{
        gap: '16px',
        alignItems: 'center'
      }}>
          <CopyToClipboardWrapper textToCopy={vault.id}>
            <Typography>{vault.id}</Typography>
          </CopyToClipboardWrapper>
        </Stack>
      </RowWrapper>
      <RowWrapper>
        <Typography sx={{
        width: '150px'
      }}>
          Color
        </Typography>
        {isEditingColor && isEditable ? <VaultColorSelectorWithButtons initialData={vault} saveData={vault_0 => {
        void updateVaultColor(vault_0).then(() => {
          setVault(vault_0);
          setIsEditingColor(false);
          onSaveSuccess();
        });
      }} onCancel={() => setIsEditingColor(false)} disabled={false} /> : <Stack direction='row' sx={{
        alignItems: 'center',
        gap: '12px'
      }}>
            <VaultColoredDot color={vault.backgroundColor} />
            {isEditable && <IconButton onClick={() => setIsEditingColor(true)} data-testid='rename-entity-name-edit-btn'>
                <i className={'material-symbols-stylus-outline w-[20px] h-[20px] text-(--mui-palette-text-primary)'} />
              </IconButton>}
          </Stack>}
      </RowWrapper>
      <RowWrapper>
        <Typography sx={{
        width: '150px'
      }}>
          Tags
        </Typography>
        {editedTags ? <Stack sx={{
        flexGrow: 1,
        gap: '16px'
      }}>
            <TagsSection onValidityChange={setAreTagsValid} tags={editedTags} initialTags={{}} keepOriginalTags={false} onChange={setEditedTags} />
            <Stack direction='row' sx={{
          gap: '8px'
        }}>
              <Button variant='contained' disabled={!areTagsValid} onClick={() => {
            const updatedVault_0 = {
              ...vault,
              tags: editedTags
            };
            void dal.vaults.update(updatedVault_0).then(() => {
              setVault(updatedVault_0);
              setEditedTags(undefined);
              onSaveSuccess();
            });
          }}>
                Save
              </Button>
              <Button variant='outlined' onClick={() => setEditedTags(undefined)}>
                Cancel
              </Button>
            </Stack>
          </Stack> : <Stack direction='row' sx={{
        alignItems: 'center',
        gap: '8px'
      }}>
            {Object.keys(vault.tags ?? {}).length ? <TagsElasticContainer tags={vault.tags ?? {}} expandable /> : <Typography>--</Typography>}
            {isEditable && <IconButton onClick={() => {
          setAreTagsValid(true);
          setEditedTags({
            ...(vault.tags ?? {})
          });
        }} data-testid='vault-tags-edit-btn'>
                <Icon iconClass='material-symbols-stylus-outline' />
              </IconButton>}
          </Stack>}
      </RowWrapper>
      <RowWrapper>
        <Typography sx={{
        width: '150px'
      }}>
          Cloud
        </Typography>
        <Typography>
          {CloudProviders[vault.vaultAttributes.cloudProvider].displayName}
        </Typography>
      </RowWrapper>
      <RowWrapper>
        <Typography sx={{
        width: '150px'
      }}>
          Region
        </Typography>
        <Typography>{vault.region}</Typography>
      </RowWrapper>
      <RowWrapper>
        <Typography sx={{
        width: '150px'
      }}>
          Type
        </Typography>
        <Typography>
          {vaultAccount && !vaultAccount.isCustomerOwned ? 'Eon-managed' : 'Self-managed'}
        </Typography>
      </RowWrapper>
      {EnableCspAssistedImmutability && vault?.enableCspAssistedImmutability && <RowWrapper>
            <Typography sx={{
        width: '150px'
      }}>Immutability</Typography>
            <Typography>CSP assisted immutability</Typography>
          </RowWrapper>}
      <RowWrapper>
        <Typography sx={{
        width: '150px'
      }}>
          {accountLabel}
        </Typography>
        {vaultAccount ? <AccountIndicator account={vaultAccount} /> : '--'}
      </RowWrapper>
      <RowWrapper>
        <Typography sx={{
        width: '150px'
      }}>
          Encryption type
        </Typography>
        <Typography>
          {hasCustomerEncryptionKey(vault) ? 'Self-managed' : 'Eon-managed'}
        </Typography>
      </RowWrapper>
      {vaultAccount?.isCustomerOwned && vault.vaultAttributes.aws?.bucketName && <RowWrapper>
            <Typography sx={{
        width: '150px',
        flexShrink: 0
      }}>
              Bucket
            </Typography>
            <Stack direction='row' sx={{
        flex: 1,
        minWidth: '0px',
        alignItems: 'center',
        gap: '16px'
      }}>
              <Stack sx={{
          flex: 1,
          minWidth: 0
        }}>
                <Stack sx={{
            width: '170px',
            maxWidth: '100%'
          }}>
                  <CopyToClipboardWrapper size='medium' textToCopy={vault.vaultAttributes.aws.bucketName}>
                    <OverflowTooltip text={vault.vaultAttributes.aws.bucketName} />
                  </CopyToClipboardWrapper>
                </Stack>
              </Stack>
              {canReapplyPolicy && <Stack sx={{
          width: '180px',
          flexShrink: 0,
          alignItems: 'flex-start'
        }}>
                  <ButtonWithIcon variant='text' icon='material-symbols-refresh-rounded' text={isReapplyingPolicy ? 'Reapplying policy...' : 'Reapply bucket policy'} loading={isReapplyingPolicy} disabled={isReapplyingPolicy} isAuthorized={isEditable} onClick={() => void reapplyBucketPolicy()} dataTestId='reapply-vault-bucket-policy' />
                </Stack>}
            </Stack>
          </RowWrapper>}
      {enableMrapForByov && vaultAccount?.isCustomerOwned && vault.vaultAttributes.aws?.mrapAlias && <RowWrapper>
            <Stack direction='row' sx={{
        gap: '16px'
      }}>
              <Typography sx={{
          width: '150px'
        }}>
                MRAP alias
              </Typography>
              <Typography>{vault.vaultAttributes.aws.mrapAlias}</Typography>
            </Stack>
          </RowWrapper>}
      {vaultAccount?.isCustomerOwned && vault.vaultAttributes.gcp?.bucketName && <RowWrapper>
            <Stack direction='row' sx={{
        gap: '16px'
      }}>
              <Typography sx={{
          width: '150px'
        }}>
                Bucket
              </Typography>
              <Typography>{vault.vaultAttributes.gcp?.bucketName}</Typography>
            </Stack>
          </RowWrapper>}
      {vaultAccount?.isCustomerOwned && vault.vaultAttributes.azure?.storageAccount && <RowWrapper>
            <Stack direction='row' sx={{
        gap: '16px'
      }}>
              <Typography sx={{
          width: '150px'
        }}>
                Storage Account
              </Typography>
              <Typography>
                {vault.vaultAttributes.azure?.storageAccount}
              </Typography>
            </Stack>
          </RowWrapper>}
      {vault.vaultAttributes.aws?.encryptionKey && <RowWrapper justifyContent='space-between'>
          <Stack direction='row' sx={{
        gap: '16px'
      }}>
            <Typography sx={{
          width: '150px'
        }}>
              KMS Key
            </Typography>
            <Stack sx={{
          width: '170px'
        }}>
              <CopyToClipboardWrapper size='medium' textToCopy={vault.vaultAttributes.aws?.encryptionKey}>
                <OverflowTooltip text={vault.vaultAttributes.aws?.encryptionKey} />
              </CopyToClipboardWrapper>
            </Stack>
          </Stack>
          <Stack sx={{
        width: '180px',
        flexShrink: 0,
        alignItems: 'flex-start'
      }}>
            <ButtonWithIcon variant='text' text='Get Suggested Policy' icon='material-symbols-data-object' onClick={onOpenKmsSuggestedPolicy} />
          </Stack>
        </RowWrapper>}
      {vault.vaultAttributes.gcp?.encryptionKey && <RowWrapper justifyContent='space-between'>
          <Stack direction='row' sx={{
        gap: '16px'
      }}>
            <Typography sx={{
          width: '150px'
        }}>
              Cloud KMS Key
            </Typography>
            <Stack sx={{
          width: '170px'
        }}>
              <OverflowTooltip text={vault.vaultAttributes.gcp?.encryptionKey} />
            </Stack>
          </Stack>
          <ButtonWithIcon variant='text' text='Get Suggested Permissions' icon='material-symbols-data-object' onClick={onOpenGcpKmsSuggestedPolicy} />
        </RowWrapper>}
      {vault.vaultAttributes.azure?.encryptionKey && <RowWrapper>
          <Stack direction='row' sx={{
        gap: '16px'
      }}>
            <Typography sx={{
          width: '150px'
        }}>
              Key Vault key
            </Typography>
            <Stack sx={{
          width: '170px'
        }}>
              <OverflowTooltip text={vault.vaultAttributes.azure?.encryptionKey} />
            </Stack>
          </Stack>
        </RowWrapper>}
    </Stack>;
};
const GcpKmsSuggestedPolicyContent = ({
  backupVault
}: {
  backupVault: BackupVault;
}) => {
  const dal = useDAL();
  const {
    data: scanningAccounts
  } = dal.cloudAccounts.scanning.useList();
  const gcpScanningAccount = scanningAccounts?.accounts.find(a => a.cloudProvider === Provider.Gcp);
  if (!gcpScanningAccount?.id) {
    return null;
  }
  return <Stack sx={{
    paddingX: '40px',
    gap: '24px',
    alignItems: 'flex-start'
  }}>
      <GcpKmsCopyablePolicy backupVault={backupVault} scanningAccountId={gcpScanningAccount.id} />
    </Stack>;
};
const RowWrapper = t0 => {
  const $ = _c(5);
  const {
    children,
    justifyContent
  } = t0;
  let t1;
  if ($[0] !== justifyContent) {
    t1 = {
      minHeight: "32px",
      flexGrow: 1,
      paddingRight: "24px",
      paddingLeft: "40px",
      gap: "16px",
      alignItems: "center",
      justifyContent
    };
    $[0] = justifyContent;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <Stack direction="row" sx={t1}>{children}</Stack>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};